import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
// Changed CloseIcon to ArrowBackIcon
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"; 
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import GlobalSkeleton from "../../components/GlobalSkeleton"; // <-- Add this import
export default function UserAddress({ open, onClose, addresses, onDelete, onEdit, onSetDefault, isLoading = false }) {
  const navigate = useNavigate();

  // Restored: State for the Delete Confirmation Dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  // Sorting: Default address still stays at the top for visual priority
  const sortedAddresses = [...(addresses || [])].sort((a, b) => {
    if (a.is_default) return -1;
    if (b.is_default) return 1;
    return 0;
  });

  // Restored: Handle opening the delete alert
  const handleDeleteClick = (addressId) => {
    setAddressToDelete(addressId);
    setDeleteConfirmOpen(true);
  };

  // Restored: Handle confirming the deletion
  const confirmDelete = () => {
    if (addressToDelete) {
      onDelete(addressToDelete);
    }
    setDeleteConfirmOpen(false);
    setAddressToDelete(null);
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            overflow: 'visible',
            bgcolor: '#FFFDF8',
            maxHeight: '90vh'
          }
        }}
      >
        {/* PREMIUM HEADER WITH BACK BUTTON */}
        <Box sx={{ 
          bgcolor: '#F8D548',
          borderRadius: '24px 24px 0 0',
          pb: 3, 
          pt: 2, 
          px: 3 
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton 
              onClick={onClose}
              sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="800" color="#4A332E">
              Your Addresses
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ px: 3, pt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => {
              onClose();
              navigate("/location");
            }}
            sx={{
              py: 1.5, 
              borderRadius: '16px',
              bgcolor: '#F8D548',
              color: '#3A2C23',
              fontWeight: '800', 
              textTransform: 'none', 
              fontSize: '1.05rem',
              boxShadow: '0 4px 12px rgba(141, 129, 97, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#F8D548',
                transform: 'translateY(-2px)'
              },
            }}
          >
            Add New Address
          </Button>
        </Box>

        <Box sx={{ mt: 1, px: 3, pb: 4, overflowY: 'auto' }}>
          {isLoading ? (
            <Box sx={{ mt: 1 }}>
              {/* Show two standard skeletons to mimic address cards loading */}
              <GlobalSkeleton type="default" />
              <GlobalSkeleton type="default" />
            </Box>
          ) : sortedAddresses.length === 0 ? (
            <Box textAlign="center" sx={{ mt: 6 }}>
              <Typography variant="h6" fontWeight="800" sx={{ color: '#3A2C23' }}>
                No addresses saved
              </Typography>
              <Typography variant="body2" sx={{ color: '#6E655E', mt: 1, fontWeight: 500 }}>
                Add an address to get your food delivered.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              {sortedAddresses.map((addr) => (
                <Box
                  key={addr.id}
                  onClick={() => {
                    // Only trigger if it isn't already the default address
                    if (!addr.is_default && onSetDefault) {
                      onSetDefault(addr.id);
                    }
                  }}
                  sx={{
                    p: 2.5,
                    borderRadius: '24px',
                    bgcolor: '#FFFFFF',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: addr.is_default
                      ? '0 8px 24px rgba(106, 158, 58, 0.52)' 
                      : '0 4px 16px rgba(214, 140, 94, 0.61)',
                    border: '1px solid',
                    borderColor: addr.is_default ? 'rgba(158, 145, 58, 0.3)' : '#ECE5DA',
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    transform: addr.is_default ? "scale(1.02)" : "scale(1)",
                    "&:hover": {
                      transform: addr.is_default ? "scale(1.02)" : "translateY(-2px)",
                      boxShadow: '0 8px 24px rgba(141, 90, 58, 0.08)',
                    },
                  }}
                >
                  {addr.is_default && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, bgcolor: '#c5e43e' }} />
                  )}

                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ flex: 1, mt: addr.is_default ? 0.5 : 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="800" sx={{ color: addr.is_default ? '#6A9E3A' : '#3A2C23', textTransform: 'uppercase' }}>
                          {addr.address_type || "ADDRESS"}
                        </Typography>
                        {addr.is_default && (
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon />}
                            label="Default"
                            sx={{
                              bgcolor: '#DDECC8',
                              color: '#6A9E3A',
                              height: 24,
                              fontWeight: '700',
                              fontSize: '0.7rem',
                              '& .MuiChip-icon': {
                                fontSize: 16,
                                color: '#6A9E3A'
                              }
                            }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{
                          color: '#6E655E',
                          lineHeight: 1.6,
                          wordBreak: "break-word"
                        }}
                      >
                        {[
                          addr.flat,
                          addr.building,
                          addr.landmark
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: addr.is_default ? 0.5 : 0 }}>
                      <IconButton size="small" onClick={() => onEdit(addr)} sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#f7ebcc' } }}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(addr.id)} sx={{ color: '#E85D3F', bgcolor: 'rgba(232, 93, 63, 0.1)', '&:hover': { bgcolor: 'rgba(232, 93, 63, 0.15)' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* PREMIUM: Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: { borderRadius: '24px', p: 1, minWidth: '300px', textAlign: 'center', bgcolor: '#FFFFFF', border: '1px solid #ECE5DA' }
        }}
      >
        <DialogTitle sx={{ fontWeight: '900', color: '#3A2C23', pb: 1 }}>
          Delete Address?
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#6E655E', fontWeight: 500 }}>
            Are you sure you want to delete this address? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDeleteConfirmOpen(false)}
              sx={{ borderRadius: '12px', color: '#6E655E', borderColor: '#dbc7a5', fontWeight: 'bold', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmDelete}
              disableElevation
              sx={{ borderRadius: '12px', bgcolor: '#6D4C41', color: '#FFFFFF', fontWeight: 'bold', textTransform: 'none', '&:hover': { bgcolor: '#c9462d' } }}
            >
              Yes, Delete
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}